import React, { useMemo, useState } from 'react';
import { Col, getItemWithExpiration, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { useAccessMode } from '@/context/AccessModeContext';
import {
    BASE_SETTLEMENT_KIND_NOTE,
    buildCategoryNameMap,
    describeSettlementBasis,
    shouldShowSettlementBasis,
    SettlementCategory,
    useSettlementCategories,
} from '@/features/settlementClassification';
import ModalCorrectSettlementBasis from './ModalCorrectSettlementBasis';

export const useSettlementBasis = (membership: any) => {
    const authObject = getItemWithExpiration('authObject');
    const centerId: string = authObject?.centerId ?? '';
    // 예외 수업료가 세 정산 방식 모두에서 계산되므로 정산 방식이 알려진 센터면 조회한다(분류가 없으면 줄은 숨는다).
    const settlementType: string | undefined = authObject?.settlementType;
    const isKnownType = settlementType === 'tier' || settlementType === 'sessionTier' || settlementType === 'base';
    const { data } = useSettlementCategories({ centerId, enabled: isKnownType });
    const categories: SettlementCategory[] = useMemo(() => data?.categories ?? [], [data?.categories]);
    const visible =
        !!membership?._id &&
        shouldShowSettlementBasis({
            settlementType,
            available: !!data?.available,
            categoryCount: categories.length,
            source: membership?.settlementIssueKindSource,
        });
    return { visible, centerId, categories, settlementType: settlementType ?? 'tier' };
};

/**
 * 수강권 카드 안의 「정산 기준 — 값」 한 줄(기존 「발급 일자」 줄과 같은 문법).
 * 호출부가 useSettlementBasis 의 visible 일 때만 그린다.
 */
const SettlementBasisRow = ({
    membership,
    isNewSales,
    centerId,
    categories,
    settlementType = 'tier',
    onCorrected,
}: {
    membership: any;
    isNewSales: boolean | null | undefined;
    centerId: string;
    categories: SettlementCategory[];
    /** 'base' 면 분류만 보이고 고친다(기본 정산은 신규/재등록을 수강권의 신규·재등록 표시로 정한다). */
    settlementType?: string;
    onCorrected: (patch: Record<string, unknown>) => void;
}) => {
    const isBase = settlementType === 'base';
    const { isReadOnly } = useAccessMode();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const categoryMap = useMemo(() => buildCategoryNameMap(categories), [categories]);
    const view = describeSettlementBasis(membership, isNewSales, categoryMap, settlementType);
    const valueText = view.recorded && !isBase ? `${view.categoryText} · ${view.kindText}` : view.categoryText;

    return (
        <Col className="w-full">
            <Row className="w-full justify-between items-center">
                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                    정산 기준
                </DemiLightText>
                <Row className="items-center gap-[8px] min-w-0">
                    <span
                        className="truncate"
                        title={view.recorded ? undefined : '이 기능 전에 발급한 수강권이에요.'}
                    >
                        <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                            {valueText}
                        </MediumText>
                    </span>
                    {membership?.settlementIssueKindSource === 'corrected' && (
                        <DemiLightText fontSize={13} color="#6B7280" className="whitespace-nowrap">
                            정정
                        </DemiLightText>
                    )}
                    {!isReadOnly && (
                        <RegularText
                            fontSize={13}
                            color={colors.primary}
                            className="cursor-pointer whitespace-nowrap hover:underline"
                            onClick={() => setIsEditOpen(true)}
                        >
                            수정
                        </RegularText>
                    )}
                </Row>
            </Row>
            {isBase && (
                <DemiLightText fontSize={13} lineHeight={18} color="#6B7280" className="mt-[2px] self-end">
                    {BASE_SETTLEMENT_KIND_NOTE}
                </DemiLightText>
            )}
            {view.mismatchNote && (
                <DemiLightText fontSize={13} lineHeight={18} color="#B45309" className="mt-[2px] self-end">
                    {view.mismatchNote}
                </DemiLightText>
            )}
            {isEditOpen && (
                <ModalCorrectSettlementBasis
                    centerId={centerId}
                    membershipId={String(membership._id)}
                    currentCategoryKey={view.categoryKey}
                    currentIssueKind={view.issueKind}
                    categories={categories}
                    issueKindRecorded={!!membership && Object.prototype.hasOwnProperty.call(membership, 'settlementIssueKind')}
                    categoryOnly={isBase}
                    onClose={() => setIsEditOpen(false)}
                    onCorrected={onCorrected}
                />
            )}
        </Col>
    );
};

export default SettlementBasisRow;
